import React from "react";
import { Button } from "../Button";
import type { DialogAction } from "../Dialog/types";

export interface DialogFooterProps {
  /**
   * Primary action button configuration
   */
  primaryAction?: DialogAction;
  /**
   * Secondary action button configuration
   */
  secondaryAction?: DialogAction;
  /**
   * Tertiary action button configuration (typically left-aligned destructive action)
   */
  tertiaryAction?: DialogAction;
  /**
   * Custom footer content above the action buttons
   */
  footerContent?: React.ReactNode;
}

/**
 * Renders an action button, optionally as a form submit button
 */
function ActionButton({
  action,
  defaultVariant,
  defaultSize = "lg",
}: {
  action: DialogAction;
  defaultVariant: DialogAction["variant"];
  defaultSize?: DialogAction["size"];
}) {
  const hasFormId = !!action.formId;

  return (
    <Button
      type={hasFormId ? "submit" : "button"}
      form={hasFormId ? action.formId : undefined}
      size={action.size || defaultSize}
      variant={action.variant || defaultVariant}
      onPress={hasFormId ? undefined : action.onPress}
      isLoading={action.isLoading}
      isDisabled={action.isDisabled}
    >
      {action.label}
    </Button>
  );
}

/**
 * DialogFooter
 *
 * Shared footer component for Dialog and Drawer.
 * Renders action buttons with consistent styling.
 *
 * Supports form integration via the `formId` prop on actions:
 * - When `formId` is set, the button becomes a submit button for that form
 * - Use `isDisabled` to control button state based on form validation
 */
export function DialogFooter({ primaryAction, secondaryAction, tertiaryAction, footerContent }: DialogFooterProps) {
  const hasActions = primaryAction || secondaryAction || tertiaryAction;

  if (!hasActions && !footerContent) {
    return null;
  }

  return (
    <div className={`flex w-full flex-col items-center justify-center border-t p-4 ${footerContent ? "h-28" : ""}`}>
      {footerContent && <div className="flex h-10 justify-center">{footerContent}</div>}
      {hasActions && (
        <div className="flex w-full items-center justify-between gap-3">
          {/* Left-aligned tertiary action (typically destructive) */}
          <div>{tertiaryAction && <ActionButton action={tertiaryAction} defaultVariant="destructive" />}</div>

          {/* Right-aligned primary and secondary actions */}
          <div className="flex items-center gap-3">
            {secondaryAction && <ActionButton action={secondaryAction} defaultVariant="secondary" />}
            {primaryAction && <ActionButton action={primaryAction} defaultVariant="default" />}
          </div>
        </div>
      )}
    </div>
  );
}
